"use client";
import { getColours, richTextHelper } from "@helpers";
import useDeviceDetect from "@helpers/deviceDetect";
import type { IContentModuleFields } from "@types-contentful";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import CTAButton from "./CTAButton";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import { usePathname } from "next/navigation";

const Container = styled.div<{
	resetModuleMarginBottom: string;
	backgroundColour: string;
}>`
  ${({ resetModuleMarginBottom }) =>
		resetModuleMarginBottom === "Yes" && "margin-bottom: 0px !important"};
  background-color: ${(props) => getColours(props.backgroundColour)};

  display: flex;
  flex-direction: column;
  padding: 40px 35px;
  p:has(b) {
    margin-top: 16px;
  }
  @media (min-width: 834px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 40px 10%;
    justify-content: center;
    gap: 250px;
    p:has(b) {
      margin-top: 8px;
    }
  }
`;

const Headline = styled.div`
  margin-bottom: 24px;
  @media (min-width: 834px) {
    h2 {
      margin-bottom: 0px;
    }
  }
`;
const Content = styled.div`
  @media (min-width: 834px) {
    max-width: 550px;
  }
`;

const SubCopy = styled.div``;

const CallToAction = styled.span<{ NoSubCopy: boolean }>`
  a {
    margin-top: ${({ NoSubCopy }) => (NoSubCopy ? "0px" : "32px")};
  }
`;
const AppContainer = styled.span`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 30px;
  margin-top: 8px;
  margin-left: auto;
  @media (min-width: 834px) {
    margin-top: 0px;
    justify-content: flex-start;
  }
  > * {
    max-width: 162px !important;
  }
  p {
    text-align: center;
    margin-top: 16px;
  }
`;
const ContentModule = ({
	name,
	removeNegativeSpacingBelowModule,
	backgroundColour,
	headline,
	subCopy,
	ctaButtonColour,
	ctaButtonLabel,
	ctaButtonUrl,
	googlePlayRating,
	googlePlayUrl,
	appStoreRating,
	appStoreUrl,
}: IContentModuleFields) => {
	const pathname = usePathname();

	const { isAndroid, isIOS, isMobile, isTablet } = useDeviceDetect();
	const mobileAndTablet = isMobile || isTablet;

	const [isIOSPhone, setIsIOSPhone] = useState(false);
	const [isAndroidPhone, setIsAndroidPhone] = useState(false);

	useEffect(() => {
		setIsIOSPhone(isIOS);
		setIsAndroidPhone(isAndroid);
	}, [isAndroid, isIOS]);

	const resetModuleMarginBottom = removeNegativeSpacingBelowModule;

	const NoSubCopy = !subCopy;

	return (
		<Container
			resetModuleMarginBottom={resetModuleMarginBottom}
			backgroundColour={backgroundColour as string}
		>
			<Headline>
				<h2>{headline}</h2>
			</Headline>
			<Content>
				{subCopy && <SubCopy>{richTextHelper(subCopy)}</SubCopy>}
				{ctaButtonUrl && ctaButtonColour && name && ctaButtonLabel && (
					<CallToAction NoSubCopy={NoSubCopy}>
						<CTAButton
							url={ctaButtonUrl}
							ctaButtonColour={ctaButtonColour}
							gtmCtaPosition={name}
							gtmCtaText={ctaButtonLabel}
						>
							{ctaButtonLabel}
						</CTAButton>
					</CallToAction>
				)}
				{mobileAndTablet && appStoreUrl && googlePlayUrl ? (
					<>
						{isIOSPhone && (
							<AppContainer>
								<div>
									<a
										href={appStoreUrl}
										target="_blank"
										rel="noreferrer"
										onClick={() => {
											mixpanelTrack("download_app_clicked", {
												cta_position: pathname,
												cta_text: "appleUrl",
											});
										}}
									>
										<Image
											alt="Get it on Appstore"
											src="/images/AppStoreBadge.png"
											width={162.5}
											height={48}
											data-gtm-cta-text="appleUrl"
											data-gtm-cta-position={name}
										/>
									</a>
									{appStoreRating && <p>{appStoreRating} stars out of 5</p>}
								</div>
							</AppContainer>
						)}
						{isAndroidPhone && (
							<AppContainer>
								<div>
									<a
										href={googlePlayUrl}
										target="_blank"
										rel="noreferrer"
										onClick={() => {
											mixpanelTrack("download_app_clicked", {
												cta_position: pathname,
												cta_text: "androidUrl",
											});
										}}
									>
										<Image
											alt="Get it on Google Play"
											src="/images/PlayStoreBadge.png"
											width={162.5}
											height={48}
											data-gtm-cta-text="androidUrl"
											data-gtm-cta-position={name}
										/>
									</a>
									{googlePlayRating && <p>{googlePlayRating} stars out of 5</p>}
								</div>
							</AppContainer>
						)}
					</>
				) : (
					<>
						{appStoreUrl && googlePlayUrl && (
							<AppContainer>
								<div>
									<a
										href={appStoreUrl}
										target="_blank"
										rel="noreferrer"
										onClick={() => {
											mixpanelTrack("download_app_clicked", {
												cta_position: pathname,
												cta_text: "appleUrl",
											});
										}}
									>
										<Image
											alt="Get it on Appstore"
											src="/images/AppStoreBadge.png"
											width={162.5}
											height={48}
											data-gtm-cta-text="appleUrl"
											data-gtm-cta-position={name}
										/>
									</a>
									{appStoreRating && <p>{appStoreRating} stars out of 5</p>}
								</div>
								<div>
									<a
										href={googlePlayUrl}
										target="_blank"
										rel="noreferrer"
										onClick={() => {
											mixpanelTrack("download_app_clicked", {
												cta_position: pathname,
												cta_text: "androidUrl",
											});
										}}
									>
										<Image
											alt="Get it on Google Play"
											src="/images/PlayStoreBadge.png"
											width={162.5}
											height={48}
											data-gtm-cta-text="androidUrl"
											data-gtm-cta-position={name}
										/>
									</a>
									{googlePlayRating && <p>{googlePlayRating} stars out of 5</p>}
								</div>
							</AppContainer>
						)}
					</>
				)}
			</Content>
		</Container>
	);
};

export default ContentModule;
